import { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { GOLD, SILVER, GREEN, RED, MUTED, BORDER, CYAN, ORANGE, PURPLE, TEXT } from '../constants';
import { fmt, fmtPct, today, monthKey, weekKey, quarterKey, yearKey, buildKPIRow } from '../utils';
import { SectionTitle, GhostBtn, ChartCard, Table, TR, TD, EmptyState } from './UI';

const PERIODS = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'];

function getKey(t, period) {
  if (period === 'Daily')     return t.date;
  if (period === 'Weekly')    return weekKey(t.date);
  if (period === 'Monthly')   return monthKey(t.date);
  if (period === 'Quarterly') return quarterKey(t.date);
  return yearKey(t.date);
}

export default function KPIReports({ transactions }) {
  const [period, setPeriod] = useState('Monthly');

  const groups = {};
  transactions.forEach((t) => {
    const k = getKey(t, period);
    if (!groups[k]) groups[k] = [];
    groups[k].push(t);
  });

  const rows = Object.entries(groups)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([label, txs]) => buildKPIRow(txs, label));

  // Chart: last 24 periods chronologically
  const chartData = [...rows].reverse().slice(-24).map((r) => ({
    period:            r.label.length > 8 ? r.label.slice(-8) : r.label,
    'Retail Sales':    r.retailSales,
    'Wholesale Sales': r.wholeSales,
    'Retail Buys':     r.retailBuys,
    'Wholesale Buys':  r.wholeBuys,
    'Margin':          r.margin,
  }));

  // Running totals
  const totals = rows.reduce((acc, r) => ({
    retailSales:  acc.retailSales  + r.retailSales,
    wholeSales:   acc.wholeSales   + r.wholeSales,
    retailBuys:   acc.retailBuys   + r.retailBuys,
    wholeBuys:    acc.wholeBuys    + r.wholeBuys,
    totalSales:   acc.totalSales   + r.totalSales,
    totalBuys:    acc.totalBuys    + r.totalBuys,
    margin:       acc.margin       + r.margin,
    txCount:      acc.txCount      + r.txCount,
  }), { retailSales: 0, wholeSales: 0, retailBuys: 0, wholeBuys: 0, totalSales: 0, totalBuys: 0, margin: 0, txCount: 0 });

  const tooltipStyle = { contentStyle: { background: '#111418', border: `1px solid #1E2530`, fontSize: 11, borderRadius: 6 } };

  return (
    <div>
      <SectionTitle
        title="KPI Reports"
        sub={`All-time · ${transactions.length} transactions · ${fmt(totals.totalSales)} in sales`}
        action={
          <div style={{ display: 'flex', gap: 6 }}>
            {PERIODS.map((p) => (
              <GhostBtn key={p} onClick={() => setPeriod(p)} active={period === p}>{p}</GhostBtn>
            ))}
          </div>
        }
      />

      {/* ALL-TIME TOTALS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'All-Time Sales',     value: fmt(totals.totalSales),  color: GREEN },
          { label: 'All-Time Purchases', value: fmt(totals.totalBuys),   color: RED },
          { label: 'All-Time Margin',    value: fmt(totals.margin),      color: totals.margin >= 0 ? GREEN : RED },
          { label: 'Overall Margin %',   value: fmtPct(totals.totalSales > 0 ? (totals.margin / totals.totalSales) * 100 : 0), color: GOLD },
        ].map((k) => (
          <div key={k.label} style={{ background: '#111418', border: `1px solid #1E2530`, borderRadius: 10, padding: '20px 24px' }}>
            <div style={{ color: MUTED, fontSize: 9, letterSpacing: 2 }}>{k.label.toUpperCase()}</div>
            <div style={{ color: k.color, fontSize: 22, fontWeight: 500, marginTop: 6 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* CHARTS */}
      {chartData.length > 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
          <ChartCard title={`${period} Revenue Breakdown (last ${chartData.length} periods)`}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                <XAxis dataKey="period" stroke={MUTED} tick={{ fontSize: 8 }} />
                <YAxis stroke={MUTED} tick={{ fontSize: 9 }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
                <Tooltip {...tooltipStyle} formatter={v=>fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="Retail Sales"    stackId="s" fill={GREEN}  />
                <Bar dataKey="Wholesale Sales" stackId="s" fill={CYAN}   radius={[3,3,0,0]} />
                <Bar dataKey="Retail Buys"     stackId="b" fill={RED}    />
                <Bar dataKey="Wholesale Buys"  stackId="b" fill={ORANGE} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title={`${period} Gross Margin`}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                <XAxis dataKey="period" stroke={MUTED} tick={{ fontSize: 8 }} />
                <YAxis stroke={MUTED} tick={{ fontSize: 9 }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
                <Tooltip {...tooltipStyle} formatter={v=>fmt(v)} />
                <Line type="monotone" dataKey="Margin" stroke={GOLD} strokeWidth={2} dot={{ fill: GOLD, r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* KPI TABLE */}
      {rows.length === 0 ? (
        <EmptyState text="No transaction data yet" />
      ) : (
        <Table headers={['Period','Txs','Retail Sales','Wholesale Sales','Total Sales','Retail Buys','Wholesale Buys','Total Buys','Gross Margin','Margin %']}>
          {rows.map((r) => (
            <TR key={r.label}>
              <TD style={{ color: GOLD, fontWeight: 500 }}>{r.label}</TD>
              <TD style={{ color: MUTED }}>{r.txCount}</TD>
              <TD style={{ color: GREEN }}>{fmt(r.retailSales)}</TD>
              <TD style={{ color: CYAN }}>{fmt(r.wholeSales)}</TD>
              <TD style={{ fontWeight: 500 }}>{fmt(r.totalSales)}</TD>
              <TD style={{ color: RED }}>{fmt(r.retailBuys)}</TD>
              <TD style={{ color: ORANGE }}>{fmt(r.wholeBuys)}</TD>
              <TD style={{ fontWeight: 500 }}>{fmt(r.totalBuys)}</TD>
              <TD style={{ color: r.margin >= 0 ? GREEN : RED, fontWeight: 500 }}>{fmt(r.margin)}</TD>
              <TD style={{ color: r.marginPct >= 0 ? GREEN : RED }}>{fmtPct(r.marginPct)}</TD>
            </TR>
          ))}
        </Table>
      )}
    </div>
  );
}
